import { DashboardChartArea } from '@/features/dashboards'
import type { ReportLayoutConfig, SectionBuild } from '@/features/reports'

// v3 `configs/MarketingInsights/PlacementsAnalysis.js` (243 ln). Mapping applied: sections
// carrying BOTH `config` and `dashboardConfig` render the trendline via module 09's
// `DashboardChartArea` (`rendererMode: 'prepend'`, same pattern as `performanceMax.layout.ts` /
// `searchAdsExcellence.layout.ts`) ABOVE their existing chart-table body; per-tab
// `chartDimensions` moves to one shared local map (the values are identical wherever a tab name
// repeats across sections); each section's `defaultDateValue: 'Last 7 Days'` hoists to its page
// tab as `defaultPreset`; `defaultChart: 'multi_metric'` (bespoke MultipleMetricChart, a
// Highcharts bar) normalizes to the v4 renderable `bar`; `fieldValues` role hints and the dead
// `chartLineColumn` drop (v4 derives chart field roles); the Gram tab's `isNgram` renderer is
// pending.

const PLACEMENT_CHART_DIMENSIONS: Record<string, string[]> = {
  Overall: ['placement_type'],
  Google: ['placement_type'],
  Meta: ['platform_position', 'category'],
  Tiktok: ['placement_type'],
  Criteo: ['category'],
  Dv360: ['placement_type'],
}

// v3 `configPlacement` (shared by Overview / Social Platform / Display Channels).
const placementBuild: SectionBuild = ({ tab }) => ({
  // v3 interpolated the raw channel — 'undefined' for the Overall tab — kept verbatim
  // (persistence/cache key).
  id: `60-${tab.reportId ?? 60}-${tab.channel ?? 'undefined'}-platform`,
  // Tabs always set reportId (60 / 19 / 45); the fallback only satisfies the type.
  reportId: tab.reportId ?? 60,
  scheduleTabs: tab.tabs ?? [],
  view: 'table',
  composition: 'chart-table',
  allowedViews: ['table', 'chart'],
  chartMetrics: ['cost', 'conversions_value', 'conversions'],
  chartDimensions: PLACEMENT_CHART_DIMENSIONS[tab.name] ?? PLACEMENT_CHART_DIMENSIONS['Overall']!,
  filters: tab.channel
    ? [{ field: 'marketing_channel', match_mode: 'equals', value: tab.channel }]
    : [],
})

// v3 `GOOGLE_ADS_PLACEMENT_PERFORMANCE_CONFIG` (static, table-only).
const placementPerformanceBuild: SectionBuild = () => ({
  id: '60-placement-performance',
  reportId: 60,
  scheduleTabs: ['google_ads_placement'],
})

// v3 `GOOGLE_ADS_PMAX_PLACEMENTS_CONFIG` — `fieldValues` drops (v4 derives chart roles).
const pmaxPlacementsBuild: SectionBuild = () => ({
  id: '60-performance-max-placements',
  reportId: 26,
  scheduleTabs: ['performance_max_placement', 'performance_max_youtube_placement'],
  view: 'table',
  composition: 'chart-table',
  allowedViews: ['table', 'chart'],
  chartType: 'pie',
  chartDimensions: ['placement_type'],
  chartMetrics: ['impressions'],
  sortField: 'impressions',
})

// v3 `GOOGLE_ADS_CATEGORY_CONFIG` — chart-first (`visibleType: 'chart'`, no table-toggle
// suppression → the toggle stays); v3 `multi_metric` is not in the v4 renderable set → `bar`.
const keywordDensityBuild: SectionBuild = () => ({
  id: '60-placement-analysis',
  reportId: 60,
  scheduleTabs: ['googleads_youtube_placement'],
  view: 'chart',
  allowedViews: ['table', 'chart'],
  chartType: 'bar',
  chartDimensions: ['category'],
  chartMetrics: ['cost', 'clicks', 'impressions', 'ctr'],
  filters: [
    { field: 'marketing_channel', match_mode: 'equals', value: 'google_ads' },
    { field: 'placement_type', match_mode: 'contains', value: 'YOUTUBE_VIDEO' },
    { field: 'category', match_mode: 'not_equals', value: 'nan' },
    { field: 'category', match_mode: 'not_equals', value: '-' },
  ],
})

export const PLACEMENT_ANALYSIS: ReportLayoutConfig = [
  {
    name: 'Overview',
    labelKey: 'marketingInsights.placementAnalysis.tabs.overview',
    // v3 section `defaultDateValue: 'Last 7 Days'` hoists here.
    defaultPreset: 'Last 7 Days',
    sections: [
      {
        id: '60-overview',
        hiddenCompare: true,
        renderer: DashboardChartArea,
        rendererMode: 'prepend',
        rendererProps: {
          defaultMetricFields: ['cost', 'cpc', 'purchase_value', 'roas'],
        },
        build: placementBuild,
        tabs: [
          {
            name: 'Overall',
            labelKey: 'marketingInsights.placementAnalysis.channels.overall',
            reportId: 60,
            tabs: ['overview'],
          },
          {
            name: 'Google',
            labelKey: 'marketingInsights.placementAnalysis.channels.google',
            channel: 'google_ads',
            reportId: 60,
            tabs: ['google_ads_placement'],
          },
          {
            name: 'Meta',
            labelKey: 'marketingInsights.placementAnalysis.channels.meta',
            channel: 'meta_ads',
            reportId: 19,
            tabs: ['metaads_analysis_placement'],
          },
          {
            name: 'Tiktok',
            labelKey: 'marketingInsights.placementAnalysis.channels.tiktok',
            channel: 'tiktok_ads',
            reportId: 45,
            tabs: ['tiktokads_demographic_placement_data'],
          },
          {
            name: 'Criteo',
            labelKey: 'marketingInsights.placementAnalysis.channels.criteo',
            channel: 'criteo',
            reportId: 60,
            tabs: ['criteo_placement'],
          },
          {
            name: 'Dv360',
            labelKey: 'marketingInsights.placementAnalysis.channels.dv360',
            channel: 'dv360',
            reportId: 60,
            tabs: ['dv360_placement'],
          },
        ],
      },
    ],
  },
  {
    name: 'Social Platform',
    labelKey: 'marketingInsights.placementAnalysis.tabs.socialPlatform',
    // v3 section `defaultDateValue: 'Last 7 Days'` hoists here.
    defaultPreset: 'Last 7 Days',
    sections: [
      {
        id: '60-social-platform',
        hiddenCompare: true,
        build: placementBuild,
        tabs: [
          {
            name: 'Meta',
            labelKey: 'marketingInsights.placementAnalysis.channels.meta',
            channel: 'meta_ads',
            reportId: 19,
            tabs: ['metaads_analysis_placement'],
          },
          {
            name: 'Tiktok',
            labelKey: 'marketingInsights.placementAnalysis.channels.tiktok',
            channel: 'tiktok_ads',
            reportId: 45,
            tabs: ['tiktokads_demographic_placement_data'],
          },
        ],
      },
    ],
  },
  {
    name: 'Display Channels',
    labelKey: 'marketingInsights.placementAnalysis.tabs.displayChannels',
    // v3 section `defaultDateValue: 'Last 7 Days'` hoists here.
    defaultPreset: 'Last 7 Days',
    sections: [
      {
        id: '60-placement-performance',
        hiddenCompare: true,
        renderer: DashboardChartArea,
        rendererMode: 'prepend',
        rendererProps: {
          defaultMetricFields: ['cost', 'cpc', 'purchase_value', 'roas'],
        },
        build: placementBuild,
        tabs: [
          {
            name: 'Overall',
            labelKey: 'marketingInsights.placementAnalysis.channels.overall',
            reportId: 60,
            tabs: ['overview'],
          },
          {
            name: 'Google',
            labelKey: 'marketingInsights.placementAnalysis.channels.google',
            channel: 'google_ads',
            reportId: 60,
            tabs: ['google_ads_placement'],
          },
          {
            name: 'Criteo',
            labelKey: 'marketingInsights.placementAnalysis.channels.criteo',
            channel: 'criteo',
            reportId: 60,
            tabs: ['criteo_placement'],
          },
          {
            name: 'Dv360',
            labelKey: 'marketingInsights.placementAnalysis.channels.dv360',
            channel: 'dv360',
            reportId: 60,
            tabs: ['dv360_placement'],
          },
        ],
      },
      {
        // v3 section had no id (the sibling section above owns '60-placement-performance');
        // suffixed for a unique anchor. NOTE: dead in v3 — neither the section nor its tab set
        // a reportId, so the schedule gate never matched; ported verbatim, not resurrected.
        id: '60-placement-performance-table',
        build: placementPerformanceBuild,
        tabs: [
          {
            name: 'Placement Performance',
            labelKey: 'marketingInsights.placementAnalysis.displayChannels.placementPerformance',
            tabs: ['google_ads_placement'],
          },
        ],
      },
      {
        id: '60-performance-max-placements',
        titleKey: 'marketingInsights.placementAnalysis.performanceMaxPlacements.title',
        reportId: 26,
        hideChannels: true,
        build: pmaxPlacementsBuild,
        tabs: [
          {
            name: 'Performance Max Placements',
            labelKey: 'marketingInsights.placementAnalysis.performanceMaxPlacements.title',
            tabs: ['performance_max_placement', 'performance_max_youtube_placement'],
          },
        ],
      },
      {
        id: '60-keyword-density',
        titleKey: 'marketingInsights.placementAnalysis.keywordDensity.title',
        reportId: 60,
        topTabMode: true,
        build: keywordDensityBuild,
        tabs: [
          {
            name: 'Gram',
            kind: 'pending',
            pendingNote: 'v3 isNgram → NgramChartArea (follow-up slice)',
            labelKey: 'marketingInsights.placementAnalysis.keywordDensity.gram',
            reportId: 60,
            tabs: ['youtube_video_ngram_data'],
          },
          {
            name: 'Category',
            labelKey: 'marketingInsights.placementAnalysis.keywordDensity.category',
            tabs: ['googleads_youtube_placement'],
          },
        ],
      },
    ],
  },
]
